/**
 * Theme Utility for Dynamic Brand Color Management
 *
 * This utility manages dynamic CSS custom properties based on brand data
 * fetched from the API. It allows the application to adapt its color scheme
 * to match each merchant's brand colors.
 */

import chroma from 'chroma-js';

import type { Brand } from '@/models';

/**
 * Generates a lighter shade (primary-50) from the base primary color (primary-500)
 * Uses chroma-js to create a very light tint by mixing with white
 * @param hexColor - Base hex color (primary-500)
 * @returns Hex color for primary-50 (very light shade)
 */
const generatePrimary50 = (hexColor: string): string => {
  // Mix 90% white with 10% of the primary color to create a very light tint
  return chroma.mix(hexColor, '#ffffff', 0.9, 'rgb').hex();
};

/**
 * Generates a darker shade (primary-600) from the base primary color (primary-500)
 * Uses chroma-js to darken the color slightly for hover states
 * @param hexColor - Base hex color (primary-500)
 * @returns Hex color for primary-600 (darker shade)
 */
const generatePrimary600 = (hexColor: string): string => {
  // Darken by 0.3 for a noticeable but not drastic darker shade
  return chroma(hexColor).darken(0.3).hex();
};

/**
 * Returns whether a color is visually dark based on luminance.
 * Useful for choosing readable foreground colors on dynamic backgrounds.
 */
export const isDarkColor = (color: string, threshold = 0.45): boolean => {
  try {
    return chroma(color).luminance() < threshold;
  } catch {
    return false;
  }
};

/**
 * Picks the most readable text color for a given background color.
 * Compares contrast ratio against light and dark candidates.
 */
export const getReadableTextColor = (
  backgroundColor: string,
  {
    lightTextColor = '#ffffff',
    darkTextColor = '#171923',
  }: {
    lightTextColor?: string;
    darkTextColor?: string;
  } = {},
): string => {
  try {
    const lightContrast = chroma.contrast(backgroundColor, lightTextColor);
    const darkContrast = chroma.contrast(backgroundColor, darkTextColor);

    if (lightContrast === darkContrast) {
      return isDarkColor(backgroundColor) ? lightTextColor : darkTextColor;
    }

    return lightContrast > darkContrast ? lightTextColor : darkTextColor;
  } catch {
    return darkTextColor;
  }
};

/**
 * Updates CSS custom properties dynamically based on brand data
 * This allows the Tailwind classes to reflect the brand's colors
 *
 * @param brand - Brand data containing primaryColor and secondaryColor
 */
export const updateBrandTheme = (brand: Brand) => {
  const root = document.documentElement;

  // Update primary colors if provided
  if (brand.primaryColor) {
    root.style.setProperty('--color-primary', brand.primaryColor);

    const onPrimary = getReadableTextColor(brand.primaryColor);
    root.style.setProperty('--color-on-primary', onPrimary);

    // Generate and set primary-50 (lightest shade)
    const primary50 = generatePrimary50(brand.primaryColor);
    root.style.setProperty('--color-primary-50', primary50);

    // Generate and set primary-600 (darker shade for hover)
    const primary600 = generatePrimary600(brand.primaryColor);
    root.style.setProperty('--color-primary-600', primary600);
  }

  // Update secondary color (used for secondary text, labels, etc.)
  if (brand.secondaryColor) {
    root.style.setProperty('--color-secondary', brand.secondaryColor);
  }

  console.log('🎨 Brand theme updated:', {
    primary: brand.primaryColor,
    'primary-50': root.style.getPropertyValue('--color-primary-50'),
    'primary-600': root.style.getPropertyValue('--color-primary-600'),
    secondary: brand.secondaryColor,
  });
};

// /**
//  * Simple color darkening utility
//  * Converts hex color to RGB, darkens it by the specified amount, and converts back to hex
//  *
//  * @param hexColor - Hex color string (e.g., '#3c3c3c')
//  * @param amount - Amount to darken (0.0 to 1.0, where 0.1 = 10% darker)
//  * @returns Darkened hex color
//  */
// const darkenColor = (hexColor: string, amount: number): string => {
//   // Remove # if present
//   const hex = hexColor.replace('#', '');

//   // Parse RGB components
//   const r = parseInt(hex.substr(0, 2), 16);
//   const g = parseInt(hex.substr(2, 2), 16);
//   const b = parseInt(hex.substr(4, 2), 16);

//   // Darken each component
//   const darkenedR = Math.max(0, Math.floor(r * (1 - amount)));
//   const darkenedG = Math.max(0, Math.floor(g * (1 - amount)));
//   const darkenedB = Math.max(0, Math.floor(b * (1 - amount)));

//   // Convert back to hex
//   const toHex = (value: number) => value.toString(16).padStart(2, '0');

//   return `#${toHex(darkenedR)}${toHex(darkenedG)}${toHex(darkenedB)}`;
// };

/**
 * Resets theme to default values (useful for testing or fallbacks)
 */
export const resetToDefaultTheme = () => {
  const root = document.documentElement;

  // Reset to default colors
  root.style.setProperty('--color-primary', '#3c3c3c');
  root.style.setProperty('--color-on-primary', '#ffffff');
  root.style.setProperty('--color-primary-dark', '#2a2a2a');
  root.style.setProperty('--color-secondary-text', '#6e798c');
  root.style.setProperty('--color-border-hover', '#6e798c');

  console.log('🎨 Theme reset to defaults');
};

/**
 * Gets current theme colors from CSS custom properties
 * Useful for debugging or state management
 */
export const getCurrentThemeColors = () => {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);

  return {
    primary: computedStyle.getPropertyValue('--color-primary').trim(),
    primaryDark: computedStyle.getPropertyValue('--color-primary-dark').trim(),
    secondaryText: computedStyle
      .getPropertyValue('--color-secondary-text')
      .trim(),
    borderHover: computedStyle.getPropertyValue('--color-border-hover').trim(),
  };
};
